import { Modal } from 'components/utility/react-modal'
import StripeCard from 'components/utility/stripe_card'
import axios from 'lib/axios'
import React, { useRef, useState } from 'react'

const STEPS = {
  HOLD_NOTICE: 'hold_notice',
  PAYMENT: 'payment',
  CONFIRM: 'confirm',
  AUTHORIZED: 'authorized',
  DECLINE: 'decline',
  ERROR: 'error',
}

const formatDeclineCode = (code) =>
  (code || '')
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')

const RefundNotice = () => (
  <div
    style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid var(--accent, #F0F0F0)', background: '#F9F9F9' }}
  >
    <div className="dome-d-flex dome-flex-column">
      <span className="dome-p3 dome-color-dark-grey dome-text-w500">deposits & refunds</span>
      <span className="dome-p3 dome-color-dark-grey">
        To initiate a deposit return and before filing a dispute with your credit issuer, contact{' '}
        <a
          href="mailto:refunds@domecile.com"
          className="dome-p3 dome-color-aqua"
          style={{ textDecorationLine: 'underline' }}
        >
          refunds@domecile.com
        </a>{' '}
        or Management.
      </span>
    </div>
  </div>
)

const StepHoldNotice = ({ total, descriptors, onCancel, onConfirm }) => (
  <div style={{ width: 380 }} className="dome-d-flex dome-flex-column dome-gap24">
    <div className="dome-d-flex dome-flex-column dome-gap12">
      <span>
        <i style={{ fontSize: '42px' }} className="fa-thin fa-credit-card-front dome-color-dark-green" />
      </span>
      <span className="dome-h2 dome-color-dark-green">authorize fees: {total}</span>
    </div>
    <div className="dome-d-flex dome-flex-column dome-gap24">
      <div className="dome-d-flex dome-flex-column dome-gap18">
        <div className="dome-d-flex dome-flex-column dome-gap6">
          <span className="dome-h5 dome-color-darkest-grey dome-text-w500">card hold notice</span>
          <span className="dome-p1 dome-color-dark-grey">
            Upon fee authorization, the following hold(s) will be placed on this credit or debit card until after the
            application package has been submitted.
          </span>
          <span className="dome-p1 dome-color-dark-grey dome-text-style-italic">
            Charges will show on your card statement as listed.
          </span>
        </div>
        <div style={{ height: 1, background: '#DBDBDB' }} />
        <div className="dome-d-flex dome-flex-column dome-gap12">
          {descriptors.map((d, i) => (
            <div key={i} className="dome-d-flex dome-flex-column dome-gap3">
              <div className="dome-d-flex dome-justify-between">
                <span className="dome-p1 dome-color-darkest-grey dome-text-w500">{d.label}</span>
                <span className="dome-p1 dome-color-dark-grey">{d.amount}</span>
              </div>
              <span className="dome-p1 dome-color-dark-grey">
                {d.isImmediate
                  ? 'This amount will be charged at submission.'
                  : 'This amount will be charged after submission by Property Management.'}
              </span>
            </div>
          ))}
        </div>
      </div>
      <RefundNotice />
    </div>
    <div className="dome-d-flex dome-justify-between">
      <button
        type="button"
        onClick={onCancel}
        className="dome-btn dome-btn-link dome-btn-link-med-grey dome-btn-link-cancel"
      >
        cancel
      </button>
      <button type="button" onClick={onConfirm} className="dome-btn dome-btn-base dome-btn-grey-stroke">
        confirm & proceed
      </button>
    </div>
  </div>
)

const StepPayment = ({ total, userPaymentMethods, stripeCardRef, onPaymentMethodChange, onSelectCard }) => (
  <div style={{ width: 380 }} className="dome-d-flex dome-flex-column dome-gap24">
    <div className="dome-d-flex dome-flex-column dome-gap12">
      <span>
        <i style={{ fontSize: '42px' }} className="fa-sharp fa-thin fa-circle-exclamation dome-color-dark-green" />
      </span>
      <span className="dome-h2 dome-color-dark-green">authorize fees: {total}</span>
    </div>
    <div className="package-form-payment" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <StripeCard ref={stripeCardRef} cards={userPaymentMethods} onChangePaymentMethod={onPaymentMethodChange} />
    </div>
    <div className="dome-d-flex dome-justify-between dome-align-center">
      <div style={{ width: 259 }}>
        <span className="dome-p3 dome-color-darkest-grey">
          See our pricing or refund policies for more information. View your{' '}
          <a href="/users/settings#panel-purchase-history" target="_blank" className="dome-p3 dome-color-aqua">
            purchase history {`>`}
          </a>
        </span>
      </div>
      <button type="button" onClick={onSelectCard} className="dome-btn dome-btn-base dome-btn-grey-stroke">
        select card
      </button>
    </div>
  </div>
)

const StepConfirm = ({
  total,
  descriptors,
  selectedPaymentMethod,
  userPaymentMethods,
  onBack,
  onAuthorize,
  isSubmitting,
}) => {
  const savedCard = userPaymentMethods.find((c) => String(c.id) === String(selectedPaymentMethod))

  return (
    <div style={{ width: 380 }} className="dome-d-flex dome-flex-column dome-gap24">
      <div className="dome-d-flex dome-flex-column dome-gap12">
        <span>
          <i style={{ fontSize: '42px' }} className="fa-sharp fa-thin fa-circle-exclamation dome-color-dark-green" />
        </span>
        <span className="dome-h2 dome-color-dark-green">authorize fees: {total}</span>
      </div>
      <div className="dome-d-flex dome-flex-column dome-gap8">
        {savedCard && (
          <span className="dome-p1 dome-color-dark-grey">Using payment method ending in: {savedCard.last_4}</span>
        )}
        <div>
          <span className="dome-p1 dome-color-dark-grey">Your card will be charged</span>
          <ul>
            {descriptors.map((d, i) => (
              <li key={i} className="dome-p1 dome-color-dark-grey">
                <span className="dome-p1 dome-color-darkest-grey dome-text-w500">{d.amount}</span>{' '}
                {d.isImmediate ? 'at submission' : 'after submission'}
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="dome-d-flex dome-justify-between">
        <button
          type="button"
          onClick={onBack}
          className="dome-btn dome-btn-link dome-btn-link-med-grey dome-btn-link-cancel"
        >
          cancel
        </button>
        <button
          type="button"
          onClick={onAuthorize}
          disabled={isSubmitting}
          className="dome-btn dome-btn-base dome-btn-grey-stroke"
        >
          {isSubmitting ? 'authorizing...' : 'authorize'}
        </button>
      </div>
    </div>
  )
}

const StepAuthorized = ({ descriptors, onDone }) => (
  <div style={{ width: 380 }} className="dome-d-flex dome-flex-column dome-gap18">
    <div className="dome-d-flex dome-align-center dome-flex-column dome-gap12">
      <span>
        <i style={{ fontSize: '42px' }} className="fa-thin fa-circle-check dome-color-dark-green" />
      </span>
      <span className="dome-h2 dome-color-dark-green">payment authorized</span>
    </div>
    <div className="dome-d-flex dome-flex-column dome-align-center dome-gap6">
      <span className="dome-p1 dome-color-dark-grey">This payment will show on your card statement as:</span>
      {descriptors.map((d, i) => (
        <span key={i} className="dome-p1 dome-color-darkest-grey dome-text-w500">
          {d.label}
        </span>
      ))}
    </div>
    <RefundNotice />
    <div className="dome-d-flex dome-justify-center">
      <button type="button" onClick={onDone} className="dome-btn dome-btn-base dome-btn-aqua-stroke">
        ok
      </button>
    </div>
  </div>
)

const StepDecline = ({ declineData, onClose }) => {
  const fees = (() => {
    try {
      return JSON.parse(declineData.fees || '[]')
    } catch {
      return []
    }
  })()
  const declineCode = declineData.declineCode || ''

  return (
    <div style={{ width: 600 }} className="dome-d-flex dome-flex-column dome-gap18">
      <div className="dome-d-flex dome-flex-column dome-gap12">
        <span>
          <i style={{ fontSize: '42px' }} className="fa-sharp fa-thin fa-circle-exclamation dome-color-red-alert" />
        </span>
        <span className="dome-h2 dome-color-darkest-grey">some fees did not authorize</span>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="table table-striped" style={{ minWidth: 400 }}>
          <thead>
            <tr>
              <td style={{ width: '30%' }}>Fee Name</td>
              <td style={{ width: '25%', textAlign: 'center' }}>Amount</td>
              <td style={{ width: '25%' }}>Auth Decline Reason</td>
              <td style={{ width: '20%' }}>Fee Status</td>
            </tr>
          </thead>
          <tbody>
            {fees.map((fee, i) => (
              <tr key={i}>
                <td>{fee.name}</td>
                <td style={{ textAlign: 'center' }}>
                  ${parseFloat((fee.amount || '0').toString().replace(/,/g, '')).toFixed(2)}
                </td>
                <td>
                  <a href="https://stripe.com/docs/declines/codes" target="_blank" rel="noreferrer">
                    {formatDeclineCode(declineCode)}
                  </a>
                </td>
                <td>Not Authorized</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <span className="dome-p1 dome-color-dark-grey">
        It looks like not all fees successfully authorized. Please make sure to contact your CC company and ensure that
        (1) your card has not reached a max daily spending limit and (2) your CC company is not blocking the
        authorization. You may then attempt to re-authorize. If you continue to experience issues, please contact{' '}
        <a
          href="https://boardpackager.freshdesk.com/support/tickets/new"
          target="_blank"
          rel="noreferrer"
          className="dome-color-aqua"
        >
          info@domecile.com
        </a>
        .
      </span>
      <div className="dome-d-flex dome-justify-center">
        <button type="button" onClick={onClose} className="dome-btn dome-btn-base dome-btn-aqua-stroke">
          ok
        </button>
      </div>
    </div>
  )
}

const StepError = ({ onRetry }) => (
  <div style={{ width: 380 }} className="dome-d-flex dome-flex-column dome-gap18">
    <div className="dome-d-flex dome-align-center dome-flex-column dome-gap12">
      <span>
        <i style={{ fontSize: '42px' }} className="fa-thin fa-circle-xmark dome-color-red-alert" />
      </span>
      <span className="dome-h2 dome-color-darkest-grey">error</span>
    </div>
    <span className="dome-p1 dome-color-dark-grey">
      An error occurred while attempting to process your authorization. For further assistance, please contact{' '}
      <a href="mailto:info@domecile.com" className="dome-color-aqua">
        info@domecile.com
      </a>
      .
    </span>
    <div className="dome-d-flex dome-justify-center">
      <button type="button" onClick={onRetry} className="dome-btn dome-btn-base dome-btn-aqua-stroke">
        ok
      </button>
    </div>
  </div>
)

export default function AuthorizeFeesModal({ total, descriptors, packageHashedId, authPath, userPaymentMethods }) {
  const [isOpen, setIsOpen] = useState(false)
  const [step, setStep] = useState(STEPS.HOLD_NOTICE)
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null)
  const [stripeToken, setStripeToken] = useState(null)
  const [declineData, setDeclineData] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const stripeCardRef = useRef(null)

  const open = () => {
    setStep(STEPS.HOLD_NOTICE)
    setStripeToken(null)
    setDeclineData(null)
    setIsOpen(true)
  }

  const close = () => setIsOpen(false)

  const closeAndReload = () => {
    setIsOpen(false)
    window.location.reload()
  }

  const handleModalClose = () => {
    if (step === STEPS.AUTHORIZED || step === STEPS.DECLINE) {
      closeAndReload()
    } else {
      close()
    }
  }

  const handleSelectCard = async () => {
    const isNewCard = !selectedPaymentMethod || selectedPaymentMethod === 'new-card'
    if (isNewCard) {
      try {
        const token = await stripeCardRef.current.generateToken()
        setStripeToken(token)
        setStep(STEPS.CONFIRM)
      } catch {
        // StripeCard renders inline validation errors; stay on PAYMENT step
      }
    } else {
      setStripeToken(null)
      setStep(STEPS.CONFIRM)
    }
  }

  const handleAuthorize = async () => {
    setIsSubmitting(true)
    try {
      const isNewCard = !selectedPaymentMethod || selectedPaymentMethod === 'new-card'
      const body = {
        hashed_id: packageHashedId,
        payment_method_id: isNewCard ? null : selectedPaymentMethod,
        stripeToken: isNewCard ? stripeToken : null,
      }
      const { data: result } = await axios.post(authPath, body)

      if (result.stripeError) {
        setStep(STEPS.ERROR)
      } else if (result.declineCode) {
        setDeclineData(result)
        setStep(STEPS.DECLINE)
      } else {
        setStep(STEPS.AUTHORIZED)
      }
    } catch {
      setStep(STEPS.ERROR)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <button type="button" className="dome-btn dome-btn-base dome-btn-green-stroke" onClick={open}>
        <span>authorize fees</span>
      </button>
      <Modal isOpen={isOpen} onClose={handleModalClose}>
        {step === STEPS.HOLD_NOTICE && (
          <StepHoldNotice
            total={total}
            descriptors={descriptors}
            onCancel={close}
            onConfirm={() => setStep(STEPS.PAYMENT)}
          />
        )}
        {step === STEPS.PAYMENT && (
          <StepPayment
            total={total}
            userPaymentMethods={userPaymentMethods}
            stripeCardRef={stripeCardRef}
            onPaymentMethodChange={setSelectedPaymentMethod}
            onSelectCard={handleSelectCard}
          />
        )}
        {step === STEPS.CONFIRM && (
          <StepConfirm
            total={total}
            descriptors={descriptors}
            selectedPaymentMethod={selectedPaymentMethod}
            userPaymentMethods={userPaymentMethods}
            onBack={() => {
              setStripeToken(null)
              setStep(STEPS.PAYMENT)
            }}
            onAuthorize={handleAuthorize}
            isSubmitting={isSubmitting}
          />
        )}
        {step === STEPS.AUTHORIZED && <StepAuthorized descriptors={descriptors} onDone={closeAndReload} />}
        {step === STEPS.DECLINE && <StepDecline declineData={declineData} onClose={closeAndReload} />}
        {step === STEPS.ERROR && <StepError onRetry={() => setStep(STEPS.CONFIRM)} />}
      </Modal>
    </>
  )
}
